import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Loader2, CheckCircle, XCircle, Shield, Target, AlertTriangle,
  Clock, Camera, CreditCard, Phone, BookOpen, Trophy,
  ChevronRight, Lock, TrendingUp, BarChart3, Eye,
  GraduationCap, FileText, AlertCircle
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { AppliedMasteryParent } from '../components/AppliedMasteryBanner';

const GOLD = '#C9A961';

const ParentPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeChild, setActiveChild] = useState(0);
  const [showAttendance, setShowAttendance] = useState(false);
  const [attendanceDetail, setAttendanceDetail] = useState(null);
  const [showStandards, setShowStandards] = useState(false);
  const [standardsDetail, setStandardsDetail] = useState(null);
  const [escalating, setEscalating] = useState(false);

  const token = localStorage.getItem('academy_token');

  const loadDashboard = useCallback(async () => {
    try {
      const res = await api.get('/school/parent/dashboard', { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/academy/login');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const loadAttendance = async (studentId) => {
    try {
      const res = await api.get(`/school/parent/attendance/${studentId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAttendanceDetail(res.data);
      setShowAttendance(true);
    } catch (err) { toast.error('Failed to load attendance'); }
  };

  const loadStandards = async (studentId) => {
    try {
      const res = await api.get(`/school/parent/standards/${studentId}`, { headers: { Authorization: `Bearer ${token}` } });
      setStandardsDetail(res.data);
      setShowStandards(true);
    } catch (err) { toast.error('Failed to load standards'); }
  };

  const escalate = async () => {
    setEscalating(true);
    try {
      const res = await api.post('/school/parent/escalate', {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
    } catch (err) { toast.error('Escalation failed'); }
    finally { setEscalating(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 uppercase tracking-[0.3em] text-xs">Loading Command Center</p>
        </div>
      </div>
    );
  }

  if (!data?.children?.length) {
    return (
      <div className="min-h-screen bg-[#08080a] pt-24 flex items-center justify-center">
        <div className="text-center max-w-md">
          <GraduationCap className="w-12 h-12 text-amber-600 mx-auto mb-4" />
          <h2 className="text-xl font-black text-white mb-2">No Students Linked</h2>
          <p className="text-gray-400 text-sm">{data?.message || 'Contact administration to link your student account.'}</p>
        </div>
      </div>
    );
  }

  const child = data.children[activeChild];
  const { student, attendance, academics, ncaa, athletics, red_flags, tuition } = child;

  return (
    <div className="min-h-screen bg-[#08080a] pt-20 pb-16" data-testid="parent-portal">
      {/* Command Bar */}
      <div className="border-b" style={{ borderColor: `${GOLD}30` }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <img src="/logos/saa-parent-mark.png" alt="Scholar Parent Academy" className="h-12 w-auto object-contain" />
              <div>
                <h1 className="text-white font-black text-lg">Parent Command Center</h1>
                <div className="text-xs text-gray-500">{data.parent.name} | Scholar Parent Academy</div>
              </div>
            </div>
            <Button onClick={escalate} disabled={escalating} className="text-black font-bold text-xs" style={{ backgroundColor: GOLD }} data-testid="escalate-btn">
              {escalating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Phone className="w-4 h-4 mr-1" />}
              Escalate to Counselor
            </Button>
          </div>

          {/* Child Tabs */}
          {data.children.length > 1 && (
            <div className="flex gap-2 mt-4">
              {data.children.map((c, i) => (
                <button key={i} onClick={() => setActiveChild(i)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${i === activeChild ? 'text-black' : 'bg-gray-900 text-gray-400 hover:text-white'}`}
                  style={i === activeChild ? { backgroundColor: GOLD } : {}}
                >{c.student.name}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Applied Mastery — Parent Header */}
        <div className="mb-6">
          <AppliedMasteryParent />
        </div>
        {/* Student Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-black" style={{ backgroundColor: `${GOLD}20`, color: GOLD }}>
            {student.name?.charAt(0)}
          </div>
          <div>
            <h2 className="text-white font-black text-xl">{student.name}</h2>
            <div className="flex items-center gap-3 text-xs">
              <span style={{ color: GOLD }} className="font-bold">Grade {student.grade_level}</span>
              <span className="text-gray-600">|</span>
              <span className="text-gray-400">{student.sport || 'Scholar-Athlete'}</span>
              <span className="text-gray-600">|</span>
              <span className="text-gray-500 font-mono">ID: {student.student_id}</span>
            </div>
          </div>
        </div>

        {/* Red Flag Banner */}
        {red_flags.length > 0 && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-testid="red-flag-banner">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-red-400 font-black text-sm uppercase tracking-wider">Academic Alert</span>
            </div>
            {red_flags.map((flag, i) => (
              <div key={i} className="text-sm text-gray-300 mb-1">
                Core GPA ({flag.data?.core_gpa}) is below the {flag.data?.threshold} threshold. Athletic privileges are suspended.
              </div>
            ))}
            <div className="mt-3 p-3 bg-gray-900/50 rounded-lg">
              <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: GOLD }}>Path to Eligibility</div>
              <div className="text-xs text-gray-400">
                Raise core GPA to {ncaa.d1_gpa_required} for CIF / high-school D1 eligibility, or {ncaa.athletic_lockout_gpa} to restore athletic features. Focus on: {Object.entries(ncaa.subject_grades || {}).filter(([_, v]) => v.gpa < 2.0).map(([s]) => s.replace('_', ' ')).join(', ') || 'all core subjects'}.
              </div>
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <Card className="bg-[#0e0e10] border-gray-800/50">
            <CardContent className="p-4">
              <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Attendance</div>
              <div className="text-2xl font-black text-white">{attendance.rate}%</div>
              <div className="text-xs mt-1">
                {attendance.checked_in_today
                  ? <span className="text-green-500 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Present today</span>
                  : <span className="text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> Not checked in</span>}
              </div>
              <button onClick={() => loadAttendance(student.id)} className="text-xs mt-2 hover:underline" style={{ color: GOLD }}>
                View Proof of Presence
              </button>
            </CardContent>
          </Card>

          <Card className="bg-[#0e0e10] border-gray-800/50">
            <CardContent className="p-4">
              <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">GPA</div>
              <div className="text-2xl font-black text-white">{academics.cumulative_gpa.toFixed(2)}</div>
              <div className="text-xs text-gray-500 mt-1">of 4.00</div>
            </CardContent>
          </Card>

          <Card className={`bg-[#0e0e10] ${ncaa.d1_eligible ? 'border-green-500/20' : ncaa.risk_level === 'lockout' ? 'border-red-500/20' : 'border-amber-500/20'}`}>
            <CardContent className="p-4">
              <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">CIF Status</div>
              <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${
                ncaa.risk_level === 'elite' ? 'bg-green-500/20 text-green-400' :
                ncaa.risk_level === 'on_track' ? 'bg-blue-500/20 text-blue-400' :
                ncaa.risk_level === 'at_risk' ? 'bg-amber-500/20 text-amber-400' :
                'bg-red-500/20 text-red-400'
              }`}>{ncaa.risk_level === 'elite' ? 'ELITE' : ncaa.risk_level === 'on_track' ? 'ON TRACK' : ncaa.risk_level === 'at_risk' ? 'AT RISK' : 'HOLD'}</span>
              <div className="text-xs text-gray-500 mt-2">Core: {ncaa.core_gpa.toFixed(2)}</div>
            </CardContent>
          </Card>

          <Card className="bg-[#0e0e10] border-gray-800/50">
            <CardContent className="p-4">
              <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Athletic Score</div>
              <div className="text-2xl font-black text-white">{athletics.best_score || '—'}%</div>
              <div className="text-xs text-gray-500 mt-1">{athletics.unlocked ? 'Scout Active' : 'Locked'}</div>
            </CardContent>
          </Card>

          <Card className="bg-[#0e0e10] border-gray-800/50">
            <CardContent className="p-4">
              <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Tuition</div>
              <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${
                tuition.status === 'active' ? 'bg-green-500/20 text-green-400' :
                tuition.status === 'trial' ? 'bg-blue-500/20 text-blue-400' :
                'bg-red-500/20 text-red-400'
              }`}>{tuition.status}</span>
              <div className="text-xs text-gray-500 mt-2">{tuition.status === 'trial' ? 'Free week active' : ''}</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Academic Compliance */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: GOLD }}>Academic Compliance</h2>
              <button onClick={() => loadStandards(student.id)} className="text-xs hover:underline" style={{ color: GOLD }}>
                View All Standards
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {academics.courses.map((course) => (
                <Card key={course.id} className="bg-[#0e0e10] border-gray-800/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: course.color }} />
                        <span className="text-white font-bold text-sm">{course.name}</span>
                      </div>
                      <span className={`text-lg font-black ${course.letter === 'N/A' ? 'text-gray-600' : 'text-white'}`}>{course.letter}</span>
                    </div>
                    <div className="text-xs text-gray-500 mb-2">{course.teacher}</div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-500">CA Standards</span>
                      <span style={{ color: GOLD }}>{course.standards_mastered}/{course.standards_total}</span>
                    </div>
                    <Progress value={course.compliance_pct} className="h-1.5" />
                    <div className="text-[10px] text-gray-600 mt-1">{course.compliance_pct}% compliant</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Missing Assignments — removed Feb '26 per founder request. */}

            {/* Athletic Performances */}
            {athletics.recent_performances.length > 0 && (
              <div>
                <h2 className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: GOLD }}>Athletic Performance</h2>
                <div className="space-y-2">
                  {athletics.recent_performances.map((p, i) => (
                    <div key={i} className="bg-[#0e0e10] border border-gray-800/50 rounded-lg p-3 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-black ${
                        p.score >= 85 ? 'bg-green-500/20 text-green-400' :
                        p.score >= 60 ? 'bg-amber-500/20 text-amber-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>{p.score}%</div>
                      <div className="flex-1">
                        <div className="text-white text-sm font-medium">{p.drill_type?.charAt(0).toUpperCase() + p.drill_type?.slice(1)} Drill</div>
                        <div className="text-xs text-gray-500">{new Date(p.created_at).toLocaleDateString()}</div>
                      </div>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                        p.recommendation === 'mastery' ? 'bg-green-500/20 text-green-400' :
                        p.recommendation === 'corrective' ? 'bg-red-500/20 text-red-400' :
                        'bg-amber-500/20 text-amber-400'
                      }`}>{p.recommendation}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            {/* Today's Proof of Presence */}
            <Card className="bg-[#0e0e10]" style={{ borderColor: `${GOLD}30` }}>
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest mb-3 font-bold" style={{ color: GOLD }}>
                  <Camera className="w-3 h-3 inline mr-1" /> Proof of Presence
                </div>
                {attendance.checked_in_today ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-400 text-sm font-bold">Verified Present</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Method: {attendance.today_verification?.method || 'manual'}
                    </div>
                    {attendance.today_verification?.confidence && (
                      <div className="text-xs text-gray-500">
                        Confidence: {(attendance.today_verification.confidence * 100).toFixed(1)}%
                      </div>
                    )}
                    <div className="text-xs text-gray-500">
                      Time: {attendance.today_check_in_time ? new Date(attendance.today_check_in_time).toLocaleTimeString() : 'N/A'}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span className="text-red-400 text-sm">Not checked in yet</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* NCAA Path */}
            <Card className="bg-[#0e0e10] border-gray-800/50">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest mb-3 font-bold" style={{ color: GOLD }}>
                  <Shield className="w-3 h-3 inline mr-1" /> CIF / High-School Eligibility
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Core GPA</span>
                    <span className="text-sm font-black text-white">{ncaa.core_gpa.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">D1 Required</span>
                    <span className={`text-xs font-bold ${ncaa.d1_eligible ? 'text-green-400' : 'text-red-400'}`}>
                      {ncaa.d1_gpa_required} {ncaa.d1_eligible ? '(MET)' : '(NOT MET)'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">D2 Required</span>
                    <span className={`text-xs font-bold ${ncaa.d2_eligible ? 'text-green-400' : 'text-red-400'}`}>
                      {ncaa.d2_gpa_required} {ncaa.d2_eligible ? '(MET)' : '(NOT MET)'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Scout Features</span>
                    <span className={`text-xs font-bold ${ncaa.athletic_unlocked ? 'text-green-400' : 'text-red-400'}`}>
                      {ncaa.athletic_unlocked ? 'ACTIVE' : 'LOCKED'}
                    </span>
                  </div>
                  <Progress value={Math.min((ncaa.core_gpa / 4.0) * 100, 100)} className="h-1.5" />
                </div>
              </CardContent>
            </Card>

            {/* Financial Office */}
            <Card className="bg-[#0e0e10] border-gray-800/50">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest mb-3 font-bold" style={{ color: GOLD }}>
                  <CreditCard className="w-3 h-3 inline mr-1" /> Financial Office
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-500">Status</span>
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${
                    tuition.status === 'active' ? 'bg-green-500/20 text-green-400' :
                    tuition.status === 'trial' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>{tuition.status}</span>
                </div>
                {tuition.status === 'trial' && (
                  <p className="text-xs text-gray-400 mb-3">Free trial week active. Tuition details will be available soon.</p>
                )}
                <div className="text-xs text-gray-600">Stripe payment management coming soon.</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Attendance Detail Dialog */}
      <Dialog open={showAttendance} onOpenChange={() => setShowAttendance(false)}>
        <DialogContent className="bg-[#08080a] border-gray-800 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-black flex items-center gap-2">
              <Camera className="w-5 h-5" style={{ color: GOLD }} /> Proof of Presence Gallery
            </DialogTitle>
          </DialogHeader>
          {attendanceDetail && (
            <div className="space-y-3 mt-4">
              <div className="flex justify-between text-sm mb-4">
                <span className="text-gray-500">Total Days: {attendanceDetail.summary.total_days}</span>
                <span className="text-gray-500">Rate: {attendanceDetail.summary.rate}%</span>
              </div>
              {attendanceDetail.proof_gallery.map((p, i) => (
                <div key={i} className={`p-3 rounded-lg border ${p.status === 'present' ? 'border-green-500/20 bg-green-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {p.status === 'present' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                      <span className="text-white text-sm font-medium">{p.date}</span>
                    </div>
                    <span className="text-xs text-gray-500">{p.check_in_time ? new Date(p.check_in_time).toLocaleTimeString() : ''}</span>
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    <span>Method: {p.verification_method}</span>
                    {p.confidence && <span>Confidence: {(p.confidence * 100).toFixed(1)}%</span>}
                    {p.face_detected !== undefined && <span>Face: {p.face_detected ? 'Yes' : 'No'}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Standards Detail Dialog */}
      <Dialog open={showStandards} onOpenChange={() => setShowStandards(false)}>
        <DialogContent className="bg-[#08080a] border-gray-800 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-black flex items-center gap-2">
              <BookOpen className="w-5 h-5" style={{ color: GOLD }} /> CA Standards Compliance
            </DialogTitle>
          </DialogHeader>
          {standardsDetail && (
            <div className="space-y-4 mt-4">
              <div className="text-center p-4 bg-gray-900/50 rounded-lg">
                <div className="text-3xl font-black text-white">{standardsDetail.overall.compliance_pct}%</div>
                <div className="text-xs text-gray-500">Overall Compliance ({standardsDetail.overall.mastered}/{standardsDetail.overall.total_standards} standards)</div>
              </div>
              {standardsDetail.subjects.map((s, i) => (
                <div key={i} className="p-3 bg-[#0e0e10] rounded-lg border border-gray-800/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-white font-bold text-sm">{s.name}</span>
                    </div>
                    <span className="text-xs" style={{ color: GOLD }}>{s.mastered}/{s.total_standards}</span>
                  </div>
                  <Progress value={s.compliance_pct} className="h-1.5 mb-2" />
                  {s.mastered_codes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {s.mastered_codes.map((code, ci) => (
                        <span key={ci} className="text-[9px] bg-green-500/10 text-green-400 px-1.5 py-0.5 rounded">{code}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentPortal;
